// useUpsell.ts
import { onMounted, Ref, ref, watch } from "vue";
import { SearchResultRecommendList } from "../services/search.service";
import { UpsellResult, UpsellService } from "../utils/API/Esales/esalesAPI";
import { lazy } from "../mixins/lazy";
import { RefSymbol } from "@vue/reactivity";

interface BaseUpsellProps {
    lazyLoad?: boolean;
    rootElement?: Ref<Element | null>;
}

interface UseUpsellProps extends BaseUpsellProps {
    listPath?: string;
    productKey?: string;
    limit?: number;
    trigger?: Ref<string | string[] | undefined>;
    fetcher: () => Promise<UpsellResult>;
}

const isArray = (value: any): value is string[] => Array.isArray(value);

// A cart refetch (e.g. re-checking inventory on order-type toggle) produces a
// new line-items array with the same product keys — that shouldn't trigger a
// fresh upsell fetch. Only an actual change in the set of variant keys should.
const sameKeys = (a: string[], b: string[]) =>
    a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');

function useUpsell({
    fetcher,
    lazyLoad = false,
    rootElement,
    trigger,
}: UseUpsellProps) {
    const upsellModel = ref<undefined | SearchResultRecommendList[]>(undefined);
    const isLoading = ref(false);

    const fetch = async () => {
        isLoading.value = true; 

        const [ok, result] = await fetcher();        
        
        if (ok) {            
            upsellModel.value = result;
        } 

        isLoading.value = false;
    };

    const handleTriggerChange = async (
        newValue: string | string[] | undefined,
        oldValue: string | string[] | undefined,
    ) => {
        if (!newValue || (isArray(newValue) && newValue.length === 0)) return;        
        if (isArray(newValue) && isArray(oldValue)) {
            if (sameKeys(newValue, oldValue)) return;
            return await fetch();
        }

        if (!isArray(newValue) && !isArray(oldValue)) {
            if (newValue === oldValue) return;
            return await fetch();
        }
    };

    if (trigger) {        
        watch(() => trigger.value, handleTriggerChange);
    } else {        
        onMounted(() =>
            lazyLoad && rootElement?.value && !upsellModel.value
                ? lazy(fetch, rootElement.value, true)
                : fetch(),
        );
    }

    return {
        upsellModel,
        isLoading,
    };
}

interface useProductPageUpsellProps extends BaseUpsellProps {
    productKey: string;
    categoryName: string;
}

export function useProductPageUpsell({
    productKey,
    categoryName,
    lazyLoad,
    rootElement,
}: useProductPageUpsellProps) {
    return useUpsell({
        productKey,
        lazyLoad,
        rootElement,
        fetcher: () =>
            UpsellService.getProductPageUpsell({
                ProductKey: productKey,
                categoryName,
                Preview: false,
            }),
    });
}

interface useListPathUpsellProps extends BaseUpsellProps {
    listPath: string;
    limit?: number;
}

export function useListPathUpsell({
    listPath,
    limit,
    lazyLoad,
    rootElement,
}: useListPathUpsellProps) {
    return useUpsell({
        lazyLoad,
        rootElement,
        fetcher: () =>
            UpsellService.getUpsellBlock({
                listPath,
                Preview: false,
                limit,
            }),
    });
}

interface useCartStatusUpsellProps extends BaseUpsellProps {
    variantKey: Ref<string>;
    categoryName: string;
    trigger?: Ref<string | undefined>;
}

export function useCartStatusUpsell({
    variantKey,
    categoryName,
    lazyLoad,
    rootElement,
    trigger,
}: useCartStatusUpsellProps) {
    return useUpsell({
        lazyLoad,
        rootElement,
        trigger,
        fetcher: () =>
            UpsellService.getAddToCartPopupUpsell({
                VariantKey: variantKey.value,
                categoryName,
                Preview: false,
            }),
    });
}

interface useCartPageUpsellProps extends BaseUpsellProps {
    variantKeys: Ref<string[]>;
    trigger?: Ref<string | string[]>;
}

export function useCartPageUpsell({
    variantKeys,
    lazyLoad,
    rootElement,
    trigger,
}: useCartPageUpsellProps) {    
    return useUpsell({
        lazyLoad,
        rootElement,
        trigger,
        fetcher: () =>
            UpsellService.getCartPageUpsell({
                VariantKeys: variantKeys.value,
                Preview: false,
            }),
    });
}
